import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Plus, Edit, Eye, Trash2, Grid, 
  Package, Tag, Image, MoreVertical, CheckCircle,
  AlertTriangle, Clock, Save, X, Upload
} from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { supabase } from '../../lib/supabase';

// Import the homepage categories as the master data
const masterCategories = [
  {
    id: 'cat-1',
    name: 'Garments & Apparel',
    slug: 'garments-apparel',
    description: 'Premium quality clothing including t-shirts, polo shirts, casual wear, and traditional garments made from finest Bangladeshi cotton.',
    image_url: 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
    product_count: 125,
    status: 'active',
    subcategories: [
      { id: 'sub-1', name: 'T-Shirts', slug: 't-shirts', product_count: 45 },
      { id: 'sub-2', name: 'Polo Shirts', slug: 'polo-shirts', product_count: 32 },
      { id: 'sub-3', name: 'Casual Wear', slug: 'casual-wear', product_count: 28 },
      { id: 'sub-4', name: 'Traditional Garments', slug: 'traditional-garments', product_count: 20 }
    ]
  },
  {
    id: 'cat-2',
    name: 'Jute & Natural Fiber',
    slug: 'jute-natural-fiber',
    description: 'Eco-friendly jute products including bags, rugs, handicrafts, and home decor items crafted by skilled artisans using traditional techniques.',
    image_url: 'https://images.pexels.com/photos/5217959/pexels-photo-5217959.jpeg',
    product_count: 95,
    status: 'active',
    subcategories: [
      { id: 'sub-5', name: 'Jute Bags', slug: 'jute-bags', product_count: 35 },
      { id: 'sub-6', name: 'Handicrafts', slug: 'handicrafts', product_count: 28 },
      { id: 'sub-7', name: 'Home Decor', slug: 'home-decor', product_count: 22 },
      { id: 'sub-8', name: 'Eco Products', slug: 'eco-products', product_count: 10 }
    ]
  },
  {
    id: 'cat-3',
    name: 'Frozen Foods',
    slug: 'frozen-foods',
    description: 'Fresh frozen seafood including hilsa fish, prawns, and other premium aquatic products sourced from Bangladesh\'s pristine waters.',
    image_url: 'https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg',
    product_count: 75,
    status: 'active',
    subcategories: [
      { id: 'sub-9', name: 'Frozen Fish', slug: 'frozen-fish', product_count: 40 },
      { id: 'sub-10', name: 'Prawns', slug: 'prawns', product_count: 20 },
      { id: 'sub-11', name: 'Marine Products', slug: 'marine-products', product_count: 15 }
    ]
  },
  {
    id: 'cat-4',
    name: 'Leather Goods',
    slug: 'leather-goods',
    description: 'Premium leather products including bags, wallets, belts, shoes, and accessories crafted with traditional techniques.',
    image_url: 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg',
    product_count: 85,
    status: 'active',
    subcategories: [
      { id: 'sub-12', name: 'Leather Bags', slug: 'leather-bags', product_count: 30 },
      { id: 'sub-13', name: 'Wallets', slug: 'wallets', product_count: 25 },
      { id: 'sub-14', name: 'Belts', slug: 'belts', product_count: 20 },
      { id: 'sub-15', name: 'Shoes', slug: 'shoes', product_count: 10 }
    ]
  },
  {
    id: 'cat-5',
    name: 'Pharmaceuticals & Health',
    slug: 'pharmaceuticals-health',
    description: 'WHO-GMP certified pharmaceutical products including generic medicines, health supplements, and medical devices.',
    image_url: 'https://images.pexels.com/photos/3683074/pexels-photo-3683074.jpeg',
    product_count: 65,
    status: 'active',
    subcategories: [
      { id: 'sub-16', name: 'Generic Medicines', slug: 'generic-medicines', product_count: 35 },
      { id: 'sub-17', name: 'Health Supplements', slug: 'health-supplements', product_count: 20 },
      { id: 'sub-18', name: 'Medical Devices', slug: 'medical-devices', product_count: 10 }
    ]
  },
  {
    id: 'cat-6',
    name: 'Scrap & Recycling',
    slug: 'scrap-recycling',
    description: 'Environmental solutions through recycling including scrap metal, plastic waste, and paper products.',
    image_url: 'https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg',
    product_count: 55,
    status: 'active',
    subcategories: [
      { id: 'sub-19', name: 'Scrap Metal', slug: 'scrap-metal', product_count: 25 },
      { id: 'sub-20', name: 'Plastic Recycling', slug: 'plastic-recycling', product_count: 20 },
      { id: 'sub-21', name: 'Paper Products', slug: 'paper-products', product_count: 10 }
    ]
  },
  {
    id: 'cat-7',
    name: 'Furniture',
    slug: 'furniture',
    description: 'Handcrafted wooden furniture and home furnishing items made from sustainable materials.',
    image_url: 'https://images.pexels.com/photos/1350789/pexels-photo-1350789.jpeg',
    product_count: 78,
    status: 'active',
    subcategories: [
      { id: 'sub-22', name: 'Wooden Furniture', slug: 'wooden-furniture', product_count: 40 },
      { id: 'sub-23', name: 'Home Furnishing', slug: 'home-furnishing', product_count: 25 },
      { id: 'sub-24', name: 'Office Furniture', slug: 'office-furniture', product_count: 13 }
    ]
  },
  {
    id: 'cat-8',
    name: 'Manpower',
    slug: 'manpower',
    description: 'Skilled workforce and human resource services for various industries and projects.',
    image_url: 'https://images.pexels.com/photos/8961251/pexels-photo-8961251.jpeg',
    product_count: 62,
    status: 'active',
    subcategories: [
      { id: 'sub-25', name: 'Construction Workers', slug: 'construction-workers', product_count: 25 },
      { id: 'sub-26', name: 'Skilled Technicians', slug: 'skilled-technicians', product_count: 20 },
      { id: 'sub-27', name: 'Administrative Staff', slug: 'administrative-staff', product_count: 17 }
    ]
  },
  {
    id: 'cat-9',
    name: 'Tourism',
    slug: 'tourism',
    description: 'Tourism services including guided tours, accommodation, and cultural experiences in Bangladesh.',
    image_url: 'https://images.pexels.com/photos/3155666/pexels-photo-3155666.jpeg',
    product_count: 45,
    status: 'active',
    subcategories: [
      { id: 'sub-28', name: 'Guided Tours', slug: 'guided-tours', product_count: 20 },
      { id: 'sub-29', name: 'Accommodation', slug: 'accommodation', product_count: 15 },
      { id: 'sub-30', name: 'Cultural Experiences', slug: 'cultural-experiences', product_count: 10 }
    ]
  },
  {
    id: 'cat-10',
    name: 'Freelancer',
    slug: 'freelancer',
    description: 'Professional freelance services including web development, graphic design, content writing, and digital marketing.',
    image_url: 'https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg',
    product_count: 92,
    status: 'active',
    subcategories: [
      { id: 'sub-31', name: 'Web Development', slug: 'web-development', product_count: 35 },
      { id: 'sub-32', name: 'Graphic Design', slug: 'graphic-design', product_count: 28 },
      { id: 'sub-33', name: 'Content Writing', slug: 'content-writing', product_count: 20 },
      { id: 'sub-34', name: 'Digital Marketing', slug: 'digital-marketing', product_count: 9 }
    ]
  }
];

const CategoryManagement = () => {
  const { user } = useSupabase();
  const [categories, setCategories] = useState(masterCategories);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    slug: '',
    description: '',
    image_url: '',
    status: 'active'
  });

  // Sync with database on component mount
  useEffect(() => {
    syncWithDatabase();
  }, []);

  const syncWithDatabase = async () => {
    try {
      setLoading(true);
      
      // Try to fetch categories from database
      const { data: dbCategories, error } = await supabase
        .from('categories')
        .select(`
          *,
          subcategories (*)
        `)
        .order('name');
      
      if (error) {
        console.warn('Database not available, using master categories:', error);
        // Use master categories as fallback
        setCategories(masterCategories);
        return;
      }
      
      if (dbCategories && dbCategories.length > 0) {
        // Use database categories if available
        setCategories(dbCategories);
      } else {
        // If no categories in database, use master categories
        setCategories(masterCategories);
        
        // Optionally sync master categories to database
        await syncMasterCategoriesToDatabase();
      }
    } catch (err) {
      console.error('Error syncing with database:', err);
      setCategories(masterCategories);
    } finally {
      setLoading(false);
    }
  };

  const syncMasterCategoriesToDatabase = async () => {
    try {
      // Insert master categories into database
      for (const category of masterCategories) {
        const { data: insertedCategory, error: categoryError } = await supabase
          .from('categories')
          .upsert({
            id: category.id,
            name: category.name,
            slug: category.slug,
            description: category.description,
            image_url: category.image_url,
            product_count: category.product_count
          }, { onConflict: 'slug' })
          .select()
          .single();
        
        if (categoryError) {
          console.error('Error inserting category:', categoryError);
          continue;
        }
        
        // Insert subcategories
        if (category.subcategories && category.subcategories.length > 0) {
          for (const subcategory of category.subcategories) {
            await supabase
              .from('subcategories')
              .upsert({
                id: subcategory.id,
                category_id: insertedCategory.id,
                name: subcategory.name,
                slug: subcategory.slug,
                product_count: subcategory.product_count
              }, { onConflict: 'slug' });
          }
        }
      }
      
      console.log('Master categories synced to database successfully');
    } catch (err) {
      console.error('Error syncing master categories to database:', err);
    }
  };

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Filter categories
  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (category.description && category.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || category.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleSelectCategory = (categoryId: string) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSelectAll = () => {
    setSelectedCategories(
      selectedCategories.length === filteredCategories.length 
        ? [] 
        : filteredCategories.map(category => category.id)
    );
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const categoryData = {
        ...newCategory,
        slug: generateSlug(newCategory.name),
        product_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Try to add to database
      try {
        const { data, error } = await supabase
          .from('categories')
          .insert(categoryData)
          .select()
          .single();
        
        if (error) throw error;
        
        // Add to local state
        setCategories([...categories, { ...data, subcategories: [] }]);
      } catch (dbError) {
        console.warn('Database insert failed, adding to local state only:', dbError);
        // Add to local state with generated ID
        const newCategoryWithId = {
          ...categoryData,
          id: `cat-${Date.now()}`,
          subcategories: []
        };
        setCategories([...categories, newCategoryWithId]);
      }
      
      // Reset form
      setNewCategory({
        name: '',
        slug: '',
        description: '',
        image_url: '',
        status: 'active'
      });
      setShowAddModal(false);
      
    } catch (err) {
      console.error('Error adding category:', err);
      alert('Failed to add category. Please try again.');
    }
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image_url: category.image_url || '',
      status: category.status || 'active'
    });
    setShowEditModal(true);
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingCategory) return;
    
    try {
      const updatedData = {
        ...newCategory,
        slug: generateSlug(newCategory.name),
        updated_at: new Date().toISOString()
      };
      
      // Try to update in database
      try {
        const { error } = await supabase
          .from('categories')
          .update(updatedData)
          .eq('id', editingCategory.id);
        
        if (error) throw error;
      } catch (dbError) {
        console.warn('Database update failed, updating local state only:', dbError);
      }
      
      // Update local state
      setCategories(categories.map(cat => 
        cat.id === editingCategory.id 
          ? { ...cat, ...updatedData }
          : cat
      ));
      
      // Reset form
      setNewCategory({
        name: '',
        slug: '',
        description: '',
        image_url: '',
        status: 'active'
      });
      setEditingCategory(null);
      setShowEditModal(false);
      
    } catch (err) {
      console.error('Error updating category:', err);
      alert('Failed to update category. Please try again.');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm('Are you sure you want to delete this category? This action cannot be undone.')) return;
    
    try {
      // Try to delete from database
      try {
        const { error } = await supabase
          .from('categories')
          .delete()
          .eq('id', categoryId);
        
        if (error) throw error;
      } catch (dbError) {
        console.warn('Database delete failed, removing from local state only:', dbError);
      }
      
      // Remove from local state
      setCategories(categories.filter(cat => cat.id !== categoryId));
      setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
      
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Failed to delete category. Please try again.');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Active
        </span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="h-3 w-3 mr-1" />
          Inactive
        </span>;
      case 'draft':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Draft
        </span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Category Management</h1>
              <p className="text-gray-600">Manage product categories and subcategories (Homepage Integration)</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={syncWithDatabase}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <Upload className="h-4 w-4" />
                <span>Sync to Database</span>
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Category</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <Grid className="h-5 w-5 text-blue-600 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Homepage Integration</h3>
              <p className="text-sm text-blue-700">
                These categories are displayed on the homepage and used throughout the site. Changes here will reflect on the frontend.
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search categories by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Grid className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Categories</p>
                <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Categories</p>
                <p className="text-2xl font-bold text-gray-900">{categories.filter(c => c.status === 'active').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Tag className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Subcategories</p>
                <p className="text-2xl font-bold text-gray-900">
                  {categories.reduce((sum, cat) => sum + (cat.subcategories?.length || 0), 0)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Package className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Products</p>
                <p className="text-2xl font-bold text-gray-900">
                  {categories.reduce((sum, cat) => sum + (cat.product_count || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Categories ({filteredCategories.length})</h2>
              {selectedCategories.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{selectedCategories.length} selected</span>
                  <button className="text-sm text-red-600 hover:text-red-700 font-medium">Delete Selected</button>
                  <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">Bulk Edit</button>
                </div>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {filteredCategories.map((category) => (
                <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <div className="relative">
                    <img 
                      src={category.image_url || 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg'} 
                      alt={category.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-2 right-2">
                      {getStatusBadge(category.status || 'active')}
                    </div>
                    <div className="absolute top-2 left-2">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => handleSelectCategory(category.id)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">{category.name}</h3>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {category.description}
                    </p>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {category.product_count || 0} products
                      </span>
                      <span className="text-gray-500">
                        {category.subcategories?.length || 0} subcategories
                      </span>
                    </div>
                    
                    {/* Subcategories Preview */}
                    {category.subcategories && category.subcategories.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex flex-wrap gap-1">
                          {category.subcategories.slice(0, 3).map((sub: any) => (
                            <span key={sub.id} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                              {sub.name}
                            </span>
                          ))}
                          {category.subcategories.length > 3 && (
                            <span className="text-xs text-gray-500">+{category.subcategories.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add New Category</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={handleAddCategory} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ 
                      ...prev, 
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter category name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={newCategory.slug}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, slug: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-gray-50"
                    placeholder="auto-generated"
                    readOnly
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newCategory.description}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter category description"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={newCategory.image_url}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, image_url: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={newCategory.status}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
                  >
                    <Save className="h-4 w-4" />
                    <span>Add Category</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Category</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCategory(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <form onSubmit={handleUpdateCategory} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ 
                      ...prev, 
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter category name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={newCategory.slug}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, slug: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-gray-50"
                    placeholder="auto-generated"
                    readOnly
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newCategory.description}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter category description"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={newCategory.image_url}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, image_url: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={newCategory.status}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingCategory(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
                  >
                    <Save className="h-4 w-4" />
                    <span>Update Category</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;